<template>
  <f7-view
    key="view-login"
    name="view-login"
  >
    <TheActivation
      v-if="appConfigStore.appActivationNeeded"
      :title-button="t('appActivation.common.activateApplication')"
      placeholder="﹡﹡﹡﹡"
      code-aplication="EMPLEADOS"
      @on-success="handleSuccessfulAppActivation"
      @no-activation="handleNoActivationClick"
    />

    <template v-if="appConfigStore.loginNeeded && !appConfigStore.appActivationNeeded">
      <TheLogin
        :title-button="t('common.signIn')"
        :placeholder-user="t('login.enterYourUsername')"
        :placeholder-pass="t('login.enterYourPassword')"
        :min-username-length="0"
        :min-password-length="0"
        :version-app="APP_VERSION"
        :end-point-for-login="authenticateUser"
        :remember-me-visivility="false"
        :show-password-visibility="true"
        :app-logo="logoAppLocal"
        @on-success="handleAuthenticationSuccess"
        @click-logo="handleResetApplicationClick"
      />

      <TheAppUpdate
        ref="appUpdateRef"
        class="update-app"
        :app-version="APP_VERSION"
        :min-erp-version="MIN_ERP_VERSION"
        :app-name="APPLICATION_NAME"
        :new-version-endpoint="checkNewAvailableVersion"
        :base-api-download-url="urlApp ?? ''"
      />

      <TheAppConfiguration
        class="configuration-app"
        @on-success="handleAppConfigurationSave"
      />
    </template>
  </f7-view>
</template>

<script setup>
// @ts-check
import {
  nextTick,
  onMounted,
  ref,
  shallowRef,
} from 'vue';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { useI18n } from 'vue-i18n';
import {
  APPLICATION_NAME,
  APP_VERSION,
  LOGO_HISPATEC,
  MIN_ERP_VERSION,
} from '@/js/common/constants';
import APIErp, {
  callCheckAppUpdate,
  callGetUserEnvironment,
  callProcessesAccessibility,
} from '@/js/services/APIErp';
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import CustomizationsController from '@/js/controllers/CustomizationsController';
import TraceabilityController from '@/js/controllers/TraceabilityController';
import CommonController from '@/js/controllers/CommonController';
import ApiContracts from '@/js/services/contracts/ApiContracts';
import { logError } from '@/js/utils/log';
import { TipoAplicacionCliente } from '@/js/common/enum';
import { ModuleProcessMappings } from '@erpagroapp/common';
import { useParamRef } from '@erpagroapp/params';
import { useSettingRef } from '@erpagroapp/settings';

import TheLogin from '@/components/TheLogin.vue';
import TheActivation from '@/components/TheActivation.vue';
import TheAppConfiguration from '@/components/TheAppConfiguration.vue';
import TheAppUpdate from '@/components/TheAppUpdate.vue';
import { useLoadConfigurationParameters } from '@/composables/useLoadConfigurationParameters';

/** @import { Ref, ShallowRef } from 'vue' */
/** @import { Modulo } from '@erpagroapp/types' */

const appConfigStore = useAppConfigStore();
const { t } = useI18n();
const {
  loadConfigurationParameters,
} = useLoadConfigurationParameters();

/** @type {ShallowRef<InstanceType<typeof TheAppUpdate> | null>} */
const appUpdateRef = shallowRef(null);
/** @type {Ref<number>} */
const clicksToAppResetThreshold = ref(10);
/** @type {Ref<number>} */
const resetRequestCount = ref(0);
/** @type {Ref<string>} */
const logoAppLocal = ref('');

const forceInformationBarVisibility = useParamRef('ForzarVisibilidadBarraInformativa');
const language = useSettingRef('App.Idioma');
const urlApp = useSettingRef('App.URLWebService');
const logoApp = useSettingRef('App.Logo');
const showInformationBar = useSettingRef('BarraInformacion.MostrarBarra');

/**
 * Retrieves the application logo. If not available, it returns the default logo.
 * @returns {string} The application logo URL or the default logo if not available.
 */
function getAppLogoURL() {
  return logoApp.value || LOGO_HISPATEC;
}

/**
 * Sets the initial app data.
 * @returns {void}
 */
function setInitialAppData() {
  appConfigStore.firstStartDateApp = new Date();
}

/**
 * Handles the event when no activation is needed.
 * @param {{ urlApp: string; logoApp: string }} configuration - Configuration data.
 * @returns {void}
 */
function handleNoActivationClick(configuration) {
  logoApp.value = logoAppLocal.value;
  urlApp.value = configuration.urlApp;
  appConfigStore.appActivationNeeded = false;
  appConfigStore.loginNeeded = true;
}

async function handleSuccessfulAppActivation() {
  appConfigStore.loginNeeded = true;
  appConfigStore.appActivationNeeded = false;

  if (logoApp.value) {
    await downloadAndSaveCompanyAppLogo(logoApp.value);
  }

  await nextTick();

  appUpdateRef.value?.checkNewAvailableAppVersion(true);
}

/**
 * Downloads and saves the client app logo.
 * @param {string} logoUrl - The URL of the logo to download.
 * @returns {Promise<void>} A promise that resolves when the logo is downloaded and saved.
 */
async function downloadAndSaveCompanyAppLogo(logoUrl) {
  try {
    const logoRes = await fetch(logoUrl);
    const blob = await logoRes.blob();

    const reader = new FileReader();

    reader.onloadend = () => {
      logoAppLocal.value = /** @type {string} */ (reader.result);
      logoApp.value = logoAppLocal.value;
    };
    reader.readAsDataURL(blob);
  } catch (error) {
    logError(error);
  }
}

/**
 * Handles the event for restoring the app.
 * @returns {void}
 */
function handleResetApplicationClick() {
  resetRequestCount.value += 1;

  if (resetRequestCount.value >= clicksToAppResetThreshold.value) {
    resetRequestCount.value = 0;
    appConfigStore.appActivationNeeded = true;
  }
}

/**
 * Check for a new version of the application.
 * @param {string} appName - The name of the application.
 * @param {string} currentVersion - The current version of the application.
 */
async function checkNewAvailableVersion(appName, currentVersion) {
  try {
    return callCheckAppUpdate(appName, currentVersion);
  } catch (error) {
    logError(error);
    return null;
  }
}

/**
 * Authenticates a user with the provided username and password.
 * @param {string} username - The user's username.
 * @param {string} password - The user's password.
 * @returns {Promise<any>} A promise that resolves with the authentication result or null in case of an error.
 */
async function authenticateUser(username, password) {
  try {
    return await APIErp.callAuthenticate(username, password);
  } catch (/** @type {any} */error) {
    const errorCode = error?.response?.status;
    let authenticationErrorMessage = t('common.info.authenticationError');
    if (errorCode === 404 || errorCode === 500 || errorCode === undefined) {
      authenticationErrorMessage = t('common.info.noConnection');
      HiNotification.Toast(authenticationErrorMessage);
    } else if (errorCode === 401) {
      authenticationErrorMessage = t('common.info.invalidUsernameOrPassword');
      HiNotification.Toast(authenticationErrorMessage);
    } else if (errorCode === 504) {
      authenticationErrorMessage = t('common.info.timeExceeded');
      HiNotification.Toast(authenticationErrorMessage);
    } else {
      HiNotification.Toast(authenticationErrorMessage);
    }
    logError(error);
    return null;
  }
}

/**
 * Handles the configuration save operation. This function is called when configuration settings are saved.
 * @returns {Promise<void>} A promise that resolves when the configuration is saved.
 */
async function handleAppConfigurationSave() {
  appConfigStore.loginNeeded = true;
  appConfigStore.appActivationNeeded = false;

  // Load the app's logo
  if (urlApp.value === '') {
    logoAppLocal.value = getAppLogoURL();
  } else {
    try {
      const { data } = await APIErp.CallObtenerLogoCliente();
      const invalidLogo = data === null || data === 'data:image/jpeg;base64,';

      logoApp.value = null;
      logoAppLocal.value = invalidLogo ? getAppLogoURL() : data;
    } catch (error) {
      logError(error);
    }
  }

  logoApp.value = logoAppLocal.value;
}

/**
 * Handles successful user authentication.
 * @param {{ userName: string; token: string }} data - User authentication data.
 * @returns {Promise<void>} A promise that resolves when the authentication is completed.
 */
async function handleAuthenticationSuccess(data) {
  const { userName, token } = data;

  appConfigStore.accessTokenERP = token;
  appConfigStore.userLoginData = {
    UserName: userName,
    loginDate: new Date(),
  };

  if (!await initializeUserSession()) {
    return;
  }
  appConfigStore.loginNeeded = false;
  setInitialAppData();

  await loadCustomizations();
  await loadConfigurationParameters();

  configureAppearance();

  // Initialize some modules
  await initializeModules();
}

/**
 * Initializes the user session.
 * @returns {Promise<boolean>} `true` if the session was successfully initialized, `false` otherwise.
 */
async function initializeUserSession() {
  if (!(await getUserEnviroment())) {
    return false;
  }

  if (!(await getUserAccesibility())) {
    return false;
  }

  return true;
}

/**
 * Retrieves the user environment and sets it in the store.
 * @returns {Promise<boolean>} A boolean indicating whether the process was successful.
 */
async function getUserEnviroment() {
  HiDialog.Preloader(true, t('login.info.loadingUserEnvironment'), false);

  let successful = false;
  let userEnvironment = null;

  try {
    const res = await callGetUserEnvironment();

    if (res === null || res.status !== 200) {
      HiNotification.Toast(t('login.info.errorLoadingUserEnvironment'));
    } else {
      successful = true;
    }

    userEnvironment = res.data;
  } catch (error) {
    logError(error);
    HiNotification.Toast(t('login.info.errorLoadingUserEnvironment'));
  } finally {
    HiDialog.Preloader(false);
    appConfigStore.userEnviroment = userEnvironment;
  }

  return successful;
}

/**
 * Fetches and sets user accessibility data.
 * @returns {Promise<boolean>} A promise that resolves with a boolean indicating the success of the operation.
 */
async function getUserAccesibility() {
  HiDialog.Preloader(true, t('login.info.loadingUserAccessibility'), false);

  let successful = false;
  /** @type {Modulo[]} */
  let userAccesibility = [];

  try {
    const res = await callProcessesAccessibility({
      applicationType: TipoAplicacionCliente.ERPAgroApp,
      language: language.value.Code,
    });

    if (res === null || res.status !== 200) {
      HiNotification.Toast(t('login.info.errorLoadingUserAccessibility'));
    } else {
      successful = true;
    }

    userAccesibility = Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    logError(error);
    HiNotification.Toast(t('login.info.errorLoadingUserAccessibility'));
  } finally {
    HiDialog.Preloader(false);
  }

  appConfigStore.userAccesivility = userAccesibility;
  return successful;
}

/**
 * Loads customizations and initializes the `CustomizationsController`.
 * @returns {Promise<void>} A promise that resolves when the customizations are loaded.
 */
async function loadCustomizations() {
  HiDialog.Preloader(true, t('login.info.loadingCustomizations'), false);

  try {
    await CustomizationsController.Initialize();
  } catch (error) {
    logError(error);
  } finally {
    HiDialog.Preloader(false);
  }
}

/**
 * Configures the appearance of the application.
 * @returns {void}
 */
function configureAppearance() {
  if (forceInformationBarVisibility.value) {
    showInformationBar.value = true;
  }
}

/**
 * Initializes the necessary modules based on the available module IDs.
 * @returns {Promise<void>} A promise that resolves when the modules are initialized.
 */
async function initializeModules() {
  CommonController.Initialize();

  // Production - Trazability
  if (appConfigStore.availableModuleIds.includes(ModuleProcessMappings.ProductionControl.Id)) {
    TraceabilityController.Initialize();
  }

  // Purchase contracts
  const commercialisationId = ModuleProcessMappings.Commercialisation.Id;
  if (appConfigStore.availableModuleIds.includes(commercialisationId)) {
    await initiatePurchaseContractsModule();
  }
}

/**
 * Initializes the Purchase Contracts module.
 * @returns {Promise<void>} A promise that resolves when the module is initialized.
 */
async function initiatePurchaseContractsModule() {
  const processId = ModuleProcessMappings.Commercialisation.Processes.PurchaseContracts.Id;
  const hasAccessToContractsProcess = appConfigStore.availableProcessIds.includes(processId);

  if (hasAccessToContractsProcess) {
    HiDialog.Preloader(true, t('commercialization.info.loadingModuleData'));
    try {
      await ApiContracts.contractsSincronizacion();
    } finally {
      HiDialog.Preloader(false);
    }
  }
}

onMounted(() => {
  logoAppLocal.value = getAppLogoURL();

  if (appConfigStore.firstStartDateApp === null) {
    setInitialAppData();
  }

  if (!appConfigStore.appActivationNeeded && appConfigStore.loginNeeded) {
    appUpdateRef.value?.checkNewAvailableAppVersion(true);
  }
});
</script>

<style scoped lang="scss">
.update-app {
  position: absolute;
  right: 65px;
  top: 12px;
}

.configuration-app {
  position: absolute;
  right: 25px;
  top: 12px;
}
</style>
